import { useEffect, useState, useRef } from "react";
import {
  X,
  Building2,
  Briefcase,
  ClipboardCheck,
  Loader2,
  Phone,
  ArrowRight,
  User,
  Target,
  BriefcaseBusiness,
} from "lucide-react";
import NotificationAlert from "../../../../CustomAlerts/NotificationAlert";
import { ConvertToDeal } from "../Services/DealService";
import { useOrganizations } from "../../../../Common/Recruitment/hooks/useOrganizations";

const ConvertToDealDrawer = ({ open, onClose, lead, onConvert }) => {
  const { organizations, error: organizationError } = useOrganizations();
  const [filteredOrganizations, setFilteredOrganizations] = useState([]);
  const [showOrganizationDropdown, setShowOrganizationDropdown] =
    useState(false);
  const [activeOrganizationIndex, setActiveOrganizationIndex] = useState(-1);

  const [alert, setAlert] = useState(null);
  const [targetOrganization, setTargetOrganization] = useState("");
  const [targetRole, setTargetRole] = useState("");
  const [loading, setLoading] = useState(false);
  const [orgFocused, setOrgFocused] = useState(false);
  const [roleFocused, setRoleFocused] = useState(false);
    const itemRefs = useRef([]);
  

  useEffect(() => {
    if (open) {
      setTargetOrganization("");
      setTargetRole("");
    }
  }, [open]);

  useEffect(() => {
    setFilteredOrganizations(organizations || []);
  }, [organizations]);

  useEffect(() => {
    if (
      activeOrganizationIndex >= 0 &&
      itemRefs.current[activeOrganizationIndex]
    ) {
      itemRefs.current[activeOrganizationIndex].scrollIntoView({
        block: "nearest",
        behavior: "smooth",
      });
    }
  }, [activeOrganizationIndex]);

  const handleClose = () => {
    setTargetOrganization("");
    setTargetRole("");
    setLoading(false);
    onClose();
  };

  const handleConvert = async () => {
    if (!targetOrganization.trim()) {
      setAlert({
        type: "warning",
        message: "Please enter target organization.",
      });
      return;
    }
    if (!targetRole.trim()) {
      setAlert({ type: "warning", message: "Please enter target role." });
      return;
    }
    try {
      setLoading(true);
      const payload = {
        leadUID: lead?.leadUID,
        targetOrganization,
        targetRole,
      };
      const res = await ConvertToDeal(payload);
      if (res?.status === "success") {
        setAlert({ type: "success", message: res?.message, duration: 1200 });
      }
      setTimeout(() => {
        handleClose();
      }, 1000);
    } catch (error) {
      setAlert({
        type: "error",
        message: error?.message || "Failed to create deal.",
      });
    } finally {
      setLoading(false);
    }
  };

  const initials = lead?.name
    ?.split(" ")
    .map((n) => n[0])
    .join("")
    .slice(0, 2)
    .toUpperCase();

  return (
    <>
      <div
        className={`fixed inset-0 z-50 transition-all duration-300 ${open ? "visible" : "invisible"}`}
      >
        {/* OVERLAY */}
        <div
          onClick={handleClose}
          className={`absolute inset-0 bg-slate-800/30 backdrop-blur-sm transition-opacity duration-300 ${open ? "opacity-100" : "opacity-0"}`}
        />

        {/* PANEL */}
        <div
          className={`fixed top-0 right-0 h-full w-full max-w-[640px] flex flex-col bg-white border-l border-slate-200 shadow-2xl
                      transition-transform duration-[350ms] ease-[cubic-bezier(0.32,0.72,0,1)]
                      ${open ? "translate-x-0" : "translate-x-full"}`}
        >
          {/* HEADER */}
          <div className="flex items-center justify-between px-5 py-4 border-b border-slate-100 bg-white flex-shrink-0">
            <div className="flex items-center gap-3">
              <div className="w-9 h-9 rounded-xl bg-blue-50 border border-blue-100 flex items-center justify-center flex-shrink-0">
                <BriefcaseBusiness size={16} className="text-blue-600" />
              </div>
              <div>
                <h2 className="text-[16px] font-bold text-slate-800 tracking-tight leading-tight">
                  Convert To Deal
                </h2>
                <p className="text-[12px] text-slate-400 mt-0.5 font-normal">
                  Create a recruitment deal from this lead
                </p>
              </div>
            </div>

            <button
              onClick={handleClose}
              className="w-8 h-8 rounded-lg hover:bg-slate-100 flex items-center justify-center transition-colors duration-150 cursor-pointer"
            >
              <X size={15} className="text-slate-400" />
            </button>
          </div>

          {/* BODY */}
          <div className="flex-1 overflow-y-auto p-4 flex flex-col gap-3">
            {lead && (
              <div className="rounded-2xl border border-slate-200 bg-white shadow-sm">
                {/* BANNER */}
                <div className="bg-blue-50 px-5 pt-5 pb-9">
                  <div className="flex items-center gap-3">
                    <div className="w-12 h-12 rounded-full bg-blue-600 flex items-center justify-center text-white font-semibold text-[14px] flex-shrink-0">
                      {initials || <User size={14} />}
                    </div>
                    <div>
                      <p className="text-[16px] font-semibold text-blue-900 leading-tight">
                        {lead?.name}
                      </p>
                      <div className="flex items-center gap-1.5 mt-0.5">
                        <Phone size={12} className="text-blue-400" />
                        <span className="text-[12.5px] text-blue-500">
                          {lead?.phoneNo || "—"}
                        </span>
                      </div>
                    </div>
                  </div>
                </div>

                {/* FLOATING STAT CHIPS */}
                <div className="grid grid-cols-3 gap-2.5 px-4 -mt-5 relative z-10">
                  {[
                    {
                      label: "Experience",
                      value: lead?.totalExperience
                        ? `${lead.totalExperience} yrs`
                        : "—",
                    },
                    { label: "Exp. CTC", value: lead?.expectedCTC || "—" },
                    { label: "Company", value: lead?.currentCompany || "—" },
                  ].map((s) => (
                    <div
                      key={s.label}
                      className="bg-white border border-slate-200 rounded-xl px-3 py-2.5 text-center shadow-sm"
                    >
                      <p className="text-[12px] text-slate-400 font-medium">
                        {s.label}
                      </p>
                      <p
                        className="text-[13px] font-semibold text-slate-800 mt-0.5 truncate"
                        title={s.value}
                      >
                        {s.value}
                      </p>
                    </div>
                  ))}
                </div>

                {/* FOOTER ROW */}
                <div className="flex items-center justify-between px-5 py-3.5 mt-1">
                  <span className="text-[12.5px] text-slate-400">
                    Mumbai, India
                  </span>
                </div>
              </div>
            )}

            {/* DEAL DETAILS */}
            <div className="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm">
              <div className="flex items-center gap-2 mb-1">
                <Target size={14} className="text-blue-600" />
                <span className="text-[14px] font-bold text-slate-700">
                  Deal Information
                </span>
              </div>
              <p className="text-[12px] text-slate-400 mb-4">
                Enter the organization and role to create this deal.
              </p>

              <div className="flex flex-col gap-3.5">
                {/* Organization */}
                <div>
                  <label className="flex items-center gap-1.5 text-[13px] font-semibold text-slate-600 mb-1.5">
                    Target Organization
                    <span className="text-red-500 text-sm leading-none">*</span>
                  </label>
                  <div
                    className={`relative rounded-xl border bg-white transition-all duration-150
                                ${
                                  orgFocused
                                    ? "border-blue-400 shadow-[0_0_0_3px_rgba(59,130,246,0.1)]"
                                    : "border-slate-200 hover:border-slate-300"
                                }
                              `}
                  >
                    <Building2
                      size={13}
                      className={`absolute left-3 top-1/2 -translate-y-1/2 pointer-events-none transition-colors duration-150
                                  ${orgFocused ? "text-blue-500" : "text-slate-400"}`}
                    />
                    <input
                      type="text"
                      value={targetOrganization}
                      placeholder="Type or select organization"
                      onFocus={() => {
                        setOrgFocused(true);
                        setShowOrganizationDropdown(true);
                        setFilteredOrganizations(organizations);
                        setActiveOrganizationIndex(-1);
                      }}
                      onBlur={() => {
                        setOrgFocused(false);

                        setTimeout(() => {
                          setShowOrganizationDropdown(false);
                        }, 150);
                      }}
                      onChange={(e) => {
                        const value = e.target.value;

                        setTargetOrganization(value);

                        const filtered = organizations.filter((org) =>
                          org.organization
                            .toLowerCase()
                            .includes(value.toLowerCase()),
                        );

                        setFilteredOrganizations(filtered);
                        setShowOrganizationDropdown(true);
                        setActiveOrganizationIndex(-1);
                      }}
                      onKeyDown={(e) => {
                        if (!showOrganizationDropdown) return;

                        if (e.key === "ArrowDown") {
                          e.preventDefault();

                          setActiveOrganizationIndex((prev) =>
                            prev < filteredOrganizations.length - 1
                              ? prev + 1
                              : 0,
                          );
                        }

                        if (e.key === "ArrowUp") {
                          e.preventDefault();

                          setActiveOrganizationIndex((prev) =>
                            prev > 0
                              ? prev - 1
                              : filteredOrganizations.length - 1,
                          );
                        }

                        if (e.key === "Enter") {
                          e.preventDefault();

                          if (
                            activeOrganizationIndex >= 0 &&
                            filteredOrganizations[activeOrganizationIndex]
                          ) {
                            setTargetOrganization(
                              filteredOrganizations[activeOrganizationIndex]
                                .organization,
                            );
                          }

                          setShowOrganizationDropdown(false);
                          setActiveOrganizationIndex(-1);
                        }

                        if (e.key === "Escape") {
                          setShowOrganizationDropdown(false);
                          setActiveOrganizationIndex(-1);
                        }
                      }}
                      className="w-full h-10 bg-transparent border-none outline-none pl-8 pr-3 text-[14px] font-medium text-slate-800 placeholder:text-slate-300 placeholder:font-normal"
                    />

                    {showOrganizationDropdown &&
                      filteredOrganizations.length > 0 && (
                        <div className="absolute left-0 right-0 top-full mt-2 z-[9999] bg-white border border-slate-200 rounded-xl shadow-2xl max-h-48 overflow-y-auto">
                          {filteredOrganizations.map((org, index) => (
                            <div
                              ref={(el) => (itemRefs.current[index] = el)}
                              key={org.organizationUID}
                              onMouseDown={() => {
                                setTargetOrganization(org.organization);
                                setShowOrganizationDropdown(false);
                                setActiveOrganizationIndex(-1);
                              }}
                              className={`px-4 py-2 text-sm cursor-pointer transition
            ${
              index === activeOrganizationIndex
                ? "bg-blue-100 text-blue-700"
                : "hover:bg-slate-100"
            }`}
                            >
                              {org.organization}
                            </div>
                          ))}
                        </div>
                      )}

                    {showOrganizationDropdown &&
                      targetOrganization &&
                      filteredOrganizations.length === 0 &&
                      organizations.length > 0 && (
                        <div className="absolute left-0 right-0 top-full mt-2 z-[9999] bg-white border border-slate-200 rounded-xl shadow-xl px-4 py-2 text-sm text-slate-500">
                          No matching organization found
                        </div>
                      )}
                      
                  </div>
                  {organizationError && (
  <p className="text-xs text-amber-600 mt-1">
    Organization suggestions could not be loaded. You can still enter the value manually.
  </p>
)}
                </div>

                {/* Role */}
                <div>
                  <label className="flex items-center gap-1.5 text-[13px] font-semibold text-slate-600 mb-1.5">
                    Target Role
                    <span className="text-red-500 text-sm leading-none">*</span>
                  </label>
                  <div
                    className={`relative rounded-xl border bg-white transition-all duration-150
                                ${
                                  roleFocused
                                    ? "border-blue-400 shadow-[0_0_0_3px_rgba(59,130,246,0.1)]"
                                    : "border-slate-200 hover:border-slate-300"
                                }
                              `}
                  >
                    <Briefcase
                      size={13}
                      className={`absolute left-3 top-1/2 -translate-y-1/2 pointer-events-none transition-colors duration-150
                        ${roleFocused ? "text-blue-500" : "text-slate-400"}`}
                    />
                    <input
                      value={targetRole}
                      onChange={(e) => setTargetRole(e.target.value)}
                      placeholder="e.g. Senior Engineer, Product Lead…"
                      onFocus={() => setRoleFocused(true)}
                      onBlur={() => setRoleFocused(false)}
                      className="w-full h-10 bg-transparent border-none outline-none pl-8 pr-3 text-[14px] font-medium text-slate-800 placeholder:text-slate-300 placeholder:font-normal"
                    />
                  </div>
                </div>
              </div>
            </div>

            {/* DEAL SUMMARY */}
            <div className="rounded-2xl border border-blue-100 bg-gradient-to-br from-blue-50/60 to-white p-4 shadow-sm">
              <div className="flex items-center gap-2 mb-3.5">
                <Briefcase size={16} className="text-blue-600" />
                <span className="text-[14px] font-bold text-slate-700">
                  Deal Summary
                </span>
              </div>

              <div className="flex flex-col gap-1.5">
                {[
                  { label: "Candidate", value: lead?.name || "—" },
                  { label: "Organization", value: targetOrganization || "—" },
                  { label: "Role", value: targetRole || "—" },
                ].map((row, i) => (
                  <div
                    key={i}
                    className="flex items-center justify-between bg-white border border-slate-100 rounded-xl px-3 py-2.5"
                  >
                    <span className="text-[13px] text-slate-500 font-medium">
                      {row.label}
                    </span>
                    <span className="text-[13px] font-semibold text-slate-800 max-w-[55%] truncate text-right">
                      {row.value}
                    </span>
                  </div>
                ))}

                <div className="flex items-center justify-between bg-white border border-slate-100 rounded-xl px-3 py-2.5">
                  <span className="text-[13px] text-slate-500 font-medium">
                    Initial Stage
                  </span>
                  <span className="inline-flex items-center gap-1.5 bg-blue-50 border border-blue-200 text-blue-600 text-[12px] font-bold px-2.5 py-1 rounded-full tracking-wide">
                    <span className="w-1.5 h-1.5 rounded-full bg-blue-500 animate-pulse" />
                    Screening
                  </span>
                </div>
              </div>
            </div>
          </div>

          {/* FOOTER */}
          <div className="flex-shrink-0 flex gap-2.5 px-4 py-3.5 bg-white border-t border-slate-100">
            <button
              onClick={handleClose}
              disabled={loading}
              className="flex-1 h-10 rounded-xl border border-slate-200 bg-white text-slate-600 text-[14px] font-semibold
                         hover:bg-slate-50 hover:border-slate-300 transition-all duration-150 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Cancel
            </button>

            <button
              onClick={handleConvert}
              disabled={loading}
              className="flex-[2] h-10 rounded-xl bg-blue-600 text-white text-[14px] font-bold flex items-center justify-center gap-2
                         shadow-sm shadow-blue-200 hover:bg-blue-700 hover:shadow-md hover:shadow-blue-200 hover:-translate-y-px
                         active:translate-y-0 transition-all duration-150 disabled:opacity-60 disabled:cursor-not-allowed disabled:translate-y-0"
            >
              {loading ? (
                <>
                  <Loader2 size={14} className="animate-spin" />
                  <span>Converting…</span>
                </>
              ) : (
                <>
                  <span>Convert To Deal</span>
                  <ArrowRight size={14} />
                </>
              )}
            </button>
          </div>
        </div>
      </div>

      {alert && (
        <NotificationAlert
          show={!!alert}
          type={alert?.type}
          message={alert?.message}
          onClose={() => setAlert(null)}
          duration={alert?.duration || 1200}
        />
      )}
    </>
  );
};

export default ConvertToDealDrawer;
